/**
 * Login Quote Service
 * 
 * Provides motivational quotes that change on EVERY LOGIN (not daily).
 * The quote persists for the entire session across all pages.
 * 
 * Usage:
 * - Call generateNewLoginQuote() on user login
 * - Call getLoginQuote() to retrieve the current session's quote
 * - Both OverviewPage and SavingsGoalsPage share the same quote
 */

// ============================================================================
// MOTIVATIONAL QUOTES - Fresh, encouraging, finance-focused
// ============================================================================

const LOGIN_QUOTES = [
  // Financial Empowerment
  { text: "Every pound you save is a step toward financial freedom! 💪", emoji: "🌟" },
  { text: "Your future self will thank you for starting today!", emoji: "🚀" },
  { text: "Small daily choices create massive results over time.", emoji: "✨" },
  { text: "You're not just budgeting — you're building your dreams.", emoji: "🏠" },
  { text: "Financial peace isn't about how much you earn, it's about how you manage.", emoji: "🧘" },
  
  // Progress & Growth
  { text: "Progress, not perfection. Every step counts!", emoji: "👣" },
  { text: "The best time to start was yesterday. The next best time is now.", emoji: "⏰" },
  { text: "You've got this! One transaction at a time.", emoji: "💰" },
  { text: "Tracking your money is the first step to controlling it.", emoji: "📊" },
  { text: "Celebrate your wins, no matter how small!", emoji: "🎉" },
  
  // Encouragement
  { text: "Every financial journey begins with a single decision.", emoji: "🛤️" },
  { text: "You're doing better than you think!", emoji: "🌈" },
  { text: "Be patient with yourself — great things take time.", emoji: "🌱" },
  { text: "Your commitment to your finances is inspiring!", emoji: "💖" },
  { text: "Today is a fresh start. Make it count!", emoji: "🌅" },
  
  // Mindset
  { text: "Wealth isn't about having a lot — it's about having options.", emoji: "🗝️" },
  { text: "The goal isn't to be rich, it's to be free.", emoji: "🦋" },
  { text: "Money is a tool. You're the architect of your life.", emoji: "🏗️" },
  { text: "Discipline today means freedom tomorrow.", emoji: "🎯" },
  { text: "Your relationship with money reflects your relationship with yourself.", emoji: "💫" },
  
  // Action-Oriented
  { text: "Let's make today productive! Start by logging one expense.", emoji: "📝" },
  { text: "A budget tells your money where to go.", emoji: "🧭" },
  { text: "Review, reflect, repeat. That's how habits form.", emoji: "🔄" },
  { text: "What gets tracked gets improved.", emoji: "📈" },
  { text: "Small wins today, big victories tomorrow!", emoji: "🏆" },
  
  // Savings Focus
  { text: "Saving isn't about restriction — it's about choices.", emoji: "💎" },
  { text: "Every saved pound is working for your future.", emoji: "⚙️" },
  { text: "Your goals are worth the daily discipline.", emoji: "🎯" },
  { text: "The secret to saving? Pay yourself first!", emoji: "🏦" },
  { text: "Watch your savings grow — one entry at a time.", emoji: "🌳" },
  
  // Seasonal/Fresh Energy
  { text: "New login, new opportunities!", emoji: "🌟" },
  { text: "Ready to take control? Let's do this!", emoji: "💪" },
  { text: "Welcome back! Your finances missed you.", emoji: "👋" },
  { text: "Another day, another chance to build wealth.", emoji: "🌞" },
  { text: "You showed up — that's half the battle!", emoji: "🙌" },
  
  // Philosophical
  { text: "The best investment you can make is in yourself.", emoji: "📚" },
  { text: "Financial literacy is the ultimate superpower.", emoji: "🦸" },
  { text: "Spend on what matters, save for what lasts.", emoji: "💡" },
  { text: "Contentment is the greatest wealth.", emoji: "☀️" },
  { text: "Money flows to those who respect it.", emoji: "💸" }
];

const STORAGE_KEY = 'finlist_login_quote';
const QUOTE_INDEX_KEY = 'finlist_login_quote_index';

// Track last shown indices to avoid immediate repetition
let recentIndices = [];
const MAX_RECENT = 10;

/**
 * Generate a new random quote on login
 * This should be called when user logs in
 * @returns {Object} The new quote object { text, emoji }
 */
export const generateNewLoginQuote = () => {
  // Get a random index, avoiding recent ones
  let index;
  let attempts = 0;
  
  do {
    index = Math.floor(Math.random() * LOGIN_QUOTES.length);
    attempts++;
  } while (recentIndices.includes(index) && attempts < 20);
  
  // Track this index
  recentIndices.push(index);
  if (recentIndices.length > MAX_RECENT) {
    recentIndices.shift();
  }
  
  const quote = LOGIN_QUOTES[index];
  
  // Store in sessionStorage so it persists across page navigations
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(quote));
    sessionStorage.setItem(QUOTE_INDEX_KEY, index.toString());
    console.log('📜 New login quote generated:', quote.text);
  } catch (e) {
    console.warn('Could not store login quote:', e);
  }
  
  return quote;
};

/**
 * Get the current session's login quote
 * If no quote exists (e.g., direct page load), generates one
 * @returns {Object} Quote object { text, emoji }
 */
export const getLoginQuote = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Could not retrieve login quote:', e);
  }
  
  // Generate a new one if none exists
  return generateNewLoginQuote();
};

/**
 * Clear the login quote (e.g., on logout)
 */
export const clearLoginQuote = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(QUOTE_INDEX_KEY);
    console.log('📜 Login quote cleared');
  } catch (e) {
    console.warn('Could not clear login quote:', e);
  }
};

/**
 * Get a formatted quote string with emoji
 * @returns {string} Formatted quote "emoji text"
 */
export const getFormattedLoginQuote = () => {
  const quote = getLoginQuote();
  return `${quote.emoji} ${quote.text}`;
};

export default {
  generateNewLoginQuote,
  getLoginQuote,
  clearLoginQuote,
  getFormattedLoginQuote
};
